import { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FileUpload from '../components/FileUpload';
import './ContactPage.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    type: 'werkgever',
    name: '',
    company: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    btw_nummer: '',
  });
  const [kvkFile, setKvkFile] = useState(null);
  const [certFiles, setCertFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const isWerknemer = formData.type === 'werknemer';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setError('');

    // Job-seekers must supply KvK-uittreksel, certificering(en) and BTW-nummer.
    if (isWerknemer) {
      if (!formData.btw_nummer.trim()) return setError('Vul uw BTW-nummer in.');
      if (!kvkFile) return setError('Upload uw KvK-uittreksel.');
      if (certFiles.length === 0) return setError('Upload minimaal één certificering.');
    }

    setSending(true);
    try {
      const fd = new FormData();
      fd.append('type', formData.type);
      fd.append('name', formData.name);
      fd.append('company', formData.company);
      fd.append('email', formData.email);
      fd.append('phone', formData.phone);
      fd.append('subject', formData.subject);
      fd.append('message', formData.message);
      if (isWerknemer) {
        fd.append('btw_nummer', formData.btw_nummer);
        fd.append('kvk', kvkFile);
        certFiles.forEach((f) => fd.append('certificeringen', f));
      }

      const res = await fetch('/api/contact', { method: 'POST', body: fd });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Er ging iets mis. Probeer het later opnieuw.');
      }

      setSubmitted(true);
      setFormData({ type: 'werkgever', name: '', company: '', email: '', phone: '', subject: '', message: '', btw_nummer: '' });
      setKvkFile(null);
      setCertFiles([]);
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="contact-main">
        <div className="contact-content">
          {/* Left Side - Contact Info */}
          <div className="contact-info-side">
            <h1>Contact</h1>
            <p className="contact-intro">Heeft u vragen of wilt u een vrijblijvend gesprek? Neem contact met ons op.</p>

            <div className="contact-info-list">
              <div className="contact-info-block">
                <div className="contact-info-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="contact-info-content">
                  <h3>Adres</h3>
                  <p>Schuttevaerweg 81</p>
                  <p>3044BA Rotterdam</p>
                </div>
              </div>

              <div className="contact-info-block">
                <div className="contact-info-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div className="contact-info-content">
                  <h3>Telefoon</h3>
                  <p><a href="tel:+31104159100">010 415 9100</a></p>
                </div>
              </div>

              <div className="contact-info-block">
                <div className="contact-info-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div className="contact-info-content">
                  <h3>E-mail</h3>
                  <p><a href="mailto:info@ozel.eu">info@ozel.eu</a></p>
                </div>
              </div>

              <div className="contact-info-block">
                <div className="contact-info-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="contact-info-content">
                  <h3>Openingstijden</h3>
                  <p>Maandag t/m Vrijdag: 08:30 - 17:00</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="contact-form-side">
            <div className="contact-form-header">
              <div className="contact-form-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <h2>Contact Formulier</h2>
                <p>Neem contact met ons op voor vragen of advies</p>
              </div>
            </div>

            <form className="contact-page-form" onSubmit={handleSubmit}>
              <div className="contact-type-toggle">
                <label className={`type-option ${formData.type === 'werkgever' ? 'active' : ''}`}>
                  <input type="radio" name="type" value="werkgever" checked={formData.type === 'werkgever'} onChange={handleChange} />
                  <span>Ik zoek personeel</span>
                </label>
                <label className={`type-option ${formData.type === 'werknemer' ? 'active' : ''}`}>
                  <input type="radio" name="type" value="werknemer" checked={formData.type === 'werknemer'} onChange={handleChange} />
                  <span>Ik zoek werk</span>
                </label>
              </div>

              <div className="form-group">
                <label>Naam</label>
                <input 
                  type="text" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                  placeholder="Uw volledige naam" 
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>E-mail</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleChange} 
                    required 
                    placeholder="uw@email.nl" 
                  />
                </div>
                <div className="form-group">
                  <label>Telefoon</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    value={formData.phone} 
                    onChange={handleChange} 
                    placeholder="+31 6 12345678" 
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Onderwerp</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Waar kunnen wij u mee helpen?"
                />
              </div>

              {isWerknemer && (
                <div className="form-group">
                  <label>BTW-nummer <span className="req-star">*</span></label>
                  <input
                    type="text"
                    name="btw_nummer"
                    value={formData.btw_nummer}
                    onChange={handleChange}
                    required
                    placeholder="NL000000000B00"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Uw bericht</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Beschrijf uw vraag zo uitgebreid mogelijk. Hoe meer informatie u geeft, hoe beter wij u kunnen helpen."
                />
              </div>

              {isWerknemer && (
                <div className="contact-uploads">
                  <FileUpload
                    label="KvK-uittreksel"
                    required
                    files={kvkFile}
                    onChange={setKvkFile}
                  />
                  <FileUpload
                    label="Certificeringen"
                    multiple
                    required
                    files={certFiles}
                    onChange={setCertFiles}
                  />
                </div>
              )}

              {error && <p className="contact-error">{error}</p>}

              <div className="contact-form-footer">
                <p className="contact-form-note">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                  Wij reageren binnen 24 uur op uw bericht
                </p>
                <button type="submit" className="contact-submit-btn" disabled={sending || submitted}>
                  {submitted ? 'Verzonden!' : sending ? 'Versturen...' : 'Verstuur Bericht'}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Map Section */}
      <section className="contact-map-section">
        <div className="contact-map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2459.5!2d4.42!3d51.88!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c434b6c0b2d7f%3A0x8e8e8e8e8e8e8e8e!2sSchuttevaerweg%2079%2C%203044%20BA%20Rotterdam!5e0!3m2!1snl!2snl!4v1234567890"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Özel Uitzendbureau Locatie"
          />
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ContactPage;
